import { javaCompare } from '../exportData.ts';
import { JS_SPACE, javaStrip } from '../javaText.ts';

/**
 * The thesaurus (docs/PARITY-STEP-6-PLAN.md section 5), ported from the desktop's
 * `thesaurus` package: Open English WordNet as the desktop boils it down (`WordNet`), and
 * synonyms for a word as it's written in a sentence (`Thesaurus`): "walked" is looked up as
 * `walk` in the past tense, so its synonyms are offered as "strode" and "ambled", and the
 * word before it settles what the form can't ("had walked" wants "stridden", "the walks" is
 * a noun). The word forms are LanguageTool's, from the table the desktop writes
 * (`WordFormsBuilder`), since LanguageTool doesn't run in a browser.
 * Checked against `schema/fixtures/thesaurus-cases.json`.
 */

export type PartOfSpeech = 'NOUN' | 'VERB' | 'ADJECTIVE' | 'ADVERB';
export const PARTS_OF_SPEECH: readonly PartOfSpeech[] = ['NOUN', 'VERB', 'ADJECTIVE', 'ADVERB'];
const POS_CODES: Readonly<Record<string, PartOfSpeech>> = { n: 'NOUN', v: 'VERB', a: 'ADJECTIVE', r: 'ADVERB' };
export const POS_LABELS: Readonly<Record<PartOfSpeech, string>> = { NOUN: 'noun', VERB: 'verb', ADJECTIVE: 'adjective', ADVERB: 'adverb' };

export type Inflection = 'BASE' | 'PLURAL' | 'PAST' | 'PAST_PARTICIPLE' | 'PRESENT_PARTICIPLE' | 'THIRD_PERSON' | 'COMPARATIVE' | 'SUPERLATIVE';

/** One meaning (a WordNet "synset"): the words that can say it, what it means, and how it's used. */
export interface Meaning {
  readonly id: number;
  readonly partOfSpeech: PartOfSpeech;
  readonly words: readonly string[];
  readonly definition: string;
  readonly example: string;
}

/** One way to read a word as written: "walked" is `walk`, a verb, in the past tense. */
export interface Reading {
  readonly lemma: string;
  readonly partOfSpeech: PartOfSpeech;
  readonly inflection: Inflection;
}

/** One meaning of a looked-up word, with what could replace it (`Sense`). */
export interface Sense {
  readonly meaning: Meaning;
  readonly reading: Reading;
  readonly synonyms: readonly string[];
  readonly similar: readonly string[];
  readonly narrower: readonly string[];
  readonly broader: readonly string[];
  readonly opposites: readonly string[];
  /** What's best offered in its place, best first: never a proper name. */
  readonly candidates: readonly string[];
}

export interface Lookup {
  readonly written: string;
  readonly senses: readonly Sense[];
}

const NONE = new Int32Array(0);

/** Java's `\s+` and `String.strip()`. */
const SPACES = new RegExp(`${JS_SPACE}+`, 'g');

/** The lookup key for a word or phrase: lower case, single spaces (`WordNet.key`). */
export const wordKey = (word: string) => javaStrip(word).replace(SPACES, ' ').toLowerCase();

const ids = (field: string): Int32Array => (field === '' ? NONE : Int32Array.from(field.split(','), Number));

/** Open English WordNet, as the desktop's `ThesaurusBuilder` boils it down (`WordNet`). */
export class WordNet {
  private readonly meanings: Meaning[] = [];
  private readonly similarIds: Int32Array[] = [];
  private readonly broaderIds: Int32Array[] = [];
  private readonly narrowerIds: Int32Array[];
  /** Per meaning: "word>opposite" pairs. */
  private readonly oppositePairs: string[][] = [];
  private readonly index = new Map<string, Int32Array>();

  /** Reads the data file (uncompressed). */
  constructor(text: string) {
    let number = 0;
    for (const line of text.split('\n')) {
      number++;
      if (line === '' || line[0] === '#') continue;
      const f = line.replace(/\r$/, '').split('\t');
      if (f[0] === 'M') {
        const pos = POS_CODES[f[1]![0]!];
        if (!pos) throw new Error(`Unreadable thesaurus line ${number}`);
        this.meanings.push({ id: this.meanings.length, partOfSpeech: pos, words: f[2]!.split('|'), definition: f[3] ?? '', example: f[4] ?? '' });
        this.similarIds.push(ids(f[5] ?? ''));
        this.broaderIds.push(ids(f[6] ?? ''));
        this.oppositePairs.push(!f[7] ? [] : f[7].split(';'));
      } else if (f[0] === 'W') {
        this.index.set(f[1]!, ids(f[2] ?? ''));
      }
    }
    const count = this.meanings.length;
    const check = (list: Int32Array) => {
      for (const id of list) if (id < 0 || id >= count) throw new Error(`The thesaurus data is inconsistent: meaning ${id} doesn't exist`);
    };
    for (const list of this.index.values()) check(list);
    this.similarIds.forEach(check);
    this.broaderIds.forEach(check);
    const counts = new Int32Array(count);
    for (const up of this.broaderIds) for (const id of up) counts[id]!++;
    this.narrowerIds = Array.from(counts, (n) => (n === 0 ? NONE : new Int32Array(n)));
    counts.fill(0);
    this.broaderIds.forEach((up, i) => {
      for (const id of up) this.narrowerIds[id]![counts[id]!++] = i;
    });
  }

  get size(): number {
    return this.meanings.length;
  }

  meaning(id: number): Meaning {
    return this.meanings[id]!;
  }

  knows(word: string): boolean {
    return this.index.has(wordKey(word));
  }

  /** Every meaning of `word` (its dictionary form), or those as one part of speech. */
  meaningsOf(word: string, partOfSpeech?: PartOfSpeech): Meaning[] {
    const found: Meaning[] = [];
    for (const id of this.index.get(wordKey(word)) ?? NONE) {
      const meaning = this.meanings[id]!;
      if (!partOfSpeech || meaning.partOfSpeech === partOfSpeech) found.push(meaning);
    }
    return found;
  }

  /** How many meanings `word` has: a stand-in for how common it is. */
  meaningCount(word: string): number {
    return (this.index.get(wordKey(word)) ?? NONE).length;
  }

  similar(meaning: Meaning): Meaning[] {
    return this.of(this.similarIds[meaning.id]!);
  }

  broader(meaning: Meaning): Meaning[] {
    return this.of(this.broaderIds[meaning.id]!);
  }

  narrower(meaning: Meaning): Meaning[] {
    return this.of(this.narrowerIds[meaning.id]!);
  }

  /** Opposites: those of `word` first, then of the meaning's other words, then (for an adjective with none) of similar meanings. */
  opposites(meaning: Meaning, word: string): string[] {
    const found = new Set<string>();
    const key = wordKey(word);
    this.collectOpposites(meaning.id, key, true, found);
    this.collectOpposites(meaning.id, key, false, found);
    if (found.size === 0 && meaning.partOfSpeech === 'ADJECTIVE') {
      for (const id of this.similarIds[meaning.id]!) this.collectOpposites(id, key, false, found);
    }
    return [...found];
  }

  private collectOpposites(id: number, key: string, ownOnly: boolean, found: Set<string>): void {
    for (const pair of this.oppositePairs[id]!) {
      const arrow = pair.indexOf('>');
      if (arrow < 0) continue;
      const own = wordKey(pair.slice(0, arrow)) === key;
      if (own === ownOnly || !ownOnly) found.add(pair.slice(arrow + 1));
    }
  }

  private of(list: Int32Array): Meaning[] {
    return Array.from(list, (id) => this.meanings[id]!);
  }
}

// ---- word forms (`WordForms`, from the desktop's table) ----

/** Penn Treebank tags, as LanguageTool writes them (`LanguageToolWordForms.reading` / `tag`). */
const TAG_READINGS: Readonly<Record<string, readonly [PartOfSpeech, Inflection]>> = {
  NN: ['NOUN', 'BASE'],
  NNS: ['NOUN', 'PLURAL'],
  VB: ['VERB', 'BASE'],
  VBP: ['VERB', 'BASE'],
  VBD: ['VERB', 'PAST'],
  VBN: ['VERB', 'PAST_PARTICIPLE'],
  VBG: ['VERB', 'PRESENT_PARTICIPLE'],
  VBZ: ['VERB', 'THIRD_PERSON'],
  JJ: ['ADJECTIVE', 'BASE'],
  JJR: ['ADJECTIVE', 'COMPARATIVE'],
  JJS: ['ADJECTIVE', 'SUPERLATIVE'],
  RB: ['ADVERB', 'BASE'],
  RBR: ['ADVERB', 'COMPARATIVE'],
  RBS: ['ADVERB', 'SUPERLATIVE'],
};

function tagOf(pos: PartOfSpeech, inflection: Inflection): string | null {
  const tags: Record<PartOfSpeech, Partial<Record<Inflection, string>>> = {
    NOUN: { BASE: 'NN', PLURAL: 'NNS' },
    VERB: { BASE: 'VB', PAST: 'VBD', PAST_PARTICIPLE: 'VBN', PRESENT_PARTICIPLE: 'VBG', THIRD_PERSON: 'VBZ' },
    ADJECTIVE: { BASE: 'JJ', COMPARATIVE: 'JJR', SUPERLATIVE: 'JJS' },
    ADVERB: { BASE: 'RB', COMPARATIVE: 'RBR', SUPERLATIVE: 'RBS' },
  };
  return tags[pos][inflection] ?? null;
}

/** LanguageTool's English word forms, read from the desktop's `word-forms-en.tsv` (uncompressed). */
export class WordForms {
  private readonly forms = new Map<string, Map<string, string[]>>();
  private readonly readingsOf = new Map<string, Reading[]>();
  private readonly frequencies = new Map<string, number>();

  constructor(text: string) {
    for (const raw of text.split('\n')) {
      if (raw === '' || raw[0] === '#') continue;
      const f = raw.replace(/\r$/, '').split('\t');
      if (f[0] === 'F') {
        const byTag = new Map<string, string[]>();
        for (const part of f.slice(2)) {
          const at = part.indexOf('=');
          byTag.set(part.slice(0, at), part.slice(at + 1).split('|'));
        }
        this.forms.set(f[1]!, byTag);
      } else if (f[0] === 'R') {
        const list: Reading[] = [];
        for (const part of f.slice(2)) {
          const at = part.lastIndexOf('/');
          const reading = TAG_READINGS[part.slice(at + 1)];
          if (reading) list.push({ lemma: part.slice(0, at).toLowerCase(), partOfSpeech: reading[0], inflection: reading[1] });
        }
        this.readingsOf.set(f[1]!, list);
      } else if (f[0] === 'Q') {
        this.frequencies.set(f[1]!, Number(f[2]));
      }
    }
  }

  /** Every way `word` can be read; empty for a word it doesn't know. */
  readings(word: string): Reading[] {
    return this.readingsOf.get(word) ?? [];
  }

  /** `lemma` put in `inflection`; empty if it doesn't know how. */
  inflect(lemma: string, pos: PartOfSpeech, inflection: Inflection): readonly string[] {
    const tag = tagOf(pos, inflection);
    return tag === null ? [] : (this.forms.get(lemma)?.get(tag) ?? []);
  }

  /** How common `word` is, from 0 (unknown or rare) upward. */
  frequency(word: string): number {
    const found = this.frequencies.get(word) ?? 0;
    return found === 0 && word.toLowerCase() !== word ? (this.frequencies.get(word.toLowerCase()) ?? 0) : found;
  }
}

// ---- the thesaurus (`Thesaurus`) ----

const MAX_SIMILAR = 40;
const MAX_NARROWER = 40;
const MAX_BROADER = 12;

const PARTICIPLE_CUES = new Set(['have', 'has', 'had', 'having', "'ve", '’ve', "'d", '’d', 'be', 'been', 'being', 'was', 'were', 'is', 'are', 'am', 'get', 'gets', 'got', 'gotten', 'getting']);
const NOUN_CUES = new Set(['a', 'an', 'the', 'this', 'that', 'these', 'those', 'my', 'your', 'his', 'her', 'its', 'our', 'their', 'some', 'any', 'no', 'every', 'each', 'many', 'few', 'several', 'much', 'of', 'another', 'such']);
const VERB_CUES = new Set(['to', 'i', 'you', 'he', 'she', 'we', 'they', 'it', 'will', 'would', 'shall', 'should', 'can', 'could', 'may', 'might', 'must', 'did', 'does', 'do', 'not', 'never', 'who']);
const MODIFIER_CUES = new Set(['very', 'so', 'too', 'quite', 'rather', 'more', 'most', 'less', 'least', 'really', 'fairly', 'is', 'was', 'are', 'were', 'seemed', 'looked', 'felt', 'became', 'be', 'been']);
const LINKS = new Set(['of', 'in', 'on', 'at', 'for', 'with', 'to', 'from', 'by', 'under', 'over', 'de', 'du', 'la', 'en']);

/** Java's `Character.isUpperCase` and `isLetter`, for one UTF-16 unit. */
const isUpper = (c: string) => /\p{Lu}/u.test(c);
const isLetter = (c: string) => /\p{L}/u.test(c);

/** A proper name ("Dail", "U.S. House"): never offered in place of an ordinary word. */
const isName = (word: string) => word.length > 0 && isUpper(word[0]!);

const readingKey = (r: Reading) => `${r.lemma}\u0000${r.partOfSpeech}\u0000${r.inflection}`;

/** `replacement` capitalized like `original` (`Casing.match`). */
export function matchCasing(original: string, replacement: string): string {
  if (replacement === '') return replacement;
  let letters = 0;
  let allUpper = true;
  for (const c of original.split('')) {
    if (isLetter(c)) {
      letters++;
      if (!isUpper(c)) allUpper = false;
    }
  }
  if (letters >= 2 && allUpper) return replacement.toUpperCase();
  const first = original.split('').findIndex(isLetter);
  if (first >= 0 && isUpper(original[first]!)) {
    const at = replacement.split('').findIndex(isLetter);
    if (at >= 0) return replacement.slice(0, at) + replacement[at]!.toUpperCase() + replacement.slice(at + 1);
  }
  return replacement;
}

/** The regular English form, for a word the dictionary doesn't know (`Thesaurus.regular`). */
export function regularForm(word: string, pos: PartOfSpeech, inflection: Inflection): string {
  if (word === '') return word;
  const lower = word.toLowerCase();
  const last = lower[lower.length - 1]!;
  const consonantY = last === 'y' && lower.length > 1 && !'aeiou'.includes(lower[lower.length - 2]!);
  switch (inflection) {
    case 'BASE':
      return word;
    case 'PLURAL':
    case 'THIRD_PERSON':
      if (/(s|x|z|ch|sh)$/.test(lower)) return `${word}es`;
      return consonantY ? `${word.slice(0, -1)}ies` : `${word}s`;
    case 'PAST':
    case 'PAST_PARTICIPLE':
      if (last === 'e') return `${word}d`;
      return consonantY ? `${word.slice(0, -1)}ied` : `${word}ed`;
    case 'PRESENT_PARTICIPLE':
      if (lower.endsWith('ie')) return `${word.slice(0, -2)}ying`;
      if (last === 'e' && !lower.endsWith('ee') && !lower.endsWith('ye') && !lower.endsWith('oe') && lower.length > 2) return `${word.slice(0, -1)}ing`;
      return `${word}ing`;
    case 'COMPARATIVE':
      return pos === 'NOUN' || pos === 'VERB' ? word : `more ${word}`;
    case 'SUPERLATIVE':
      return pos === 'NOUN' || pos === 'VERB' ? word : `most ${word}`;
  }
}

export class Thesaurus {
  constructor(
    readonly wordNet: WordNet,
    private readonly forms: WordForms,
  ) {}

  /** What the thesaurus has for `written` (a word or short phrase), where `before` is the word in front of it. */
  lookUp(written: string, before: string | null): Lookup {
    const text = javaStrip(written ?? '').replace(SPACES, ' ');
    if (text === '') return { written, senses: [] };
    const cue = before === null ? '' : javaStrip(before).toLowerCase();
    const senses: Sense[] = [];
    const seen = new Set<number>();
    for (const reading of this.readings(text, cue)) {
      for (const meaning of this.wordNet.meaningsOf(reading.lemma, reading.partOfSpeech)) {
        if (!seen.has(meaning.id)) {
          seen.add(meaning.id);
          senses.push(this.sense(meaning, reading, text));
        }
      }
    }
    return { written: text, senses };
  }

  /** `word` written to take the place of `original`, in the form `reading` says it was, capitalized the same way. */
  fit(word: string, reading: Reading | null, original: string): string {
    const inflected = reading === null || reading.inflection === 'BASE' ? word : this.inflect(word, reading);
    return matchCasing(original, inflected);
  }

  /** The ways `text` can be read that WordNet has meanings for, likeliest first. */
  readings(text: string, cue: string): Reading[] {
    const lower = text.toLowerCase();
    const candidates: Reading[] = [];
    if (!text.includes(' ')) {
      let tagged = this.forms.readings(text);
      if (tagged.length === 0 && lower !== text) tagged = this.forms.readings(lower);
      candidates.push(...tagged);
    } else {
      const firstSpace = lower.indexOf(' ');
      const lastSpace = lower.lastIndexOf(' ');
      for (const first of this.forms.readings(lower.slice(0, firstSpace))) {
        if (first.partOfSpeech === 'VERB' && first.inflection !== 'BASE') candidates.push({ lemma: first.lemma + lower.slice(firstSpace), partOfSpeech: 'VERB', inflection: first.inflection });
      }
      for (const last of this.forms.readings(lower.slice(lastSpace + 1))) {
        if (last.partOfSpeech === 'NOUN' && last.inflection === 'PLURAL') candidates.push({ lemma: lower.slice(0, lastSpace + 1) + last.lemma, partOfSpeech: 'NOUN', inflection: 'PLURAL' });
      }
    }
    for (const pos of PARTS_OF_SPEECH) {
      if (this.wordNet.meaningsOf(lower, pos).length > 0) candidates.push({ lemma: lower, partOfSpeech: pos, inflection: 'BASE' });
    }
    const merged = new Map<string, Reading>();
    for (const reading of candidates) {
      if (this.wordNet.meaningsOf(reading.lemma, reading.partOfSpeech).length === 0) continue;
      const key = `${reading.lemma}\u0000${reading.partOfSpeech}`;
      const kept = merged.get(key);
      if (!kept || preferred(reading.inflection, cue) < preferred(kept.inflection, cue)) merged.set(key, reading);
    }
    const readings = [...merged.values()];
    readings.sort(
      (a, b) =>
        scoreReading(b, lower, cue) - scoreReading(a, lower, cue) ||
        this.wordNet.meaningsOf(b.lemma, b.partOfSpeech).length - this.wordNet.meaningsOf(a.lemma, a.partOfSpeech).length ||
        PARTS_OF_SPEECH.indexOf(a.partOfSpeech) - PARTS_OF_SPEECH.indexOf(b.partOfSpeech),
    );
    return readings;
  }

  private sense(meaning: Meaning, reading: Reading, written: string): Sense {
    const used = new Set([wordKey(reading.lemma), wordKey(written)]);
    const scores = new Map<string, number>();
    const synonyms = fresh(meaning.words, used, Infinity);
    const similar = fresh(this.ranked(this.wordNet.similar(meaning), reading, scores), used, MAX_SIMILAR);
    const narrower = fresh(this.ranked(this.wordNet.narrower(meaning), reading, scores), used, MAX_NARROWER);
    const broader = fresh(
      this.wordNet.broader(meaning).flatMap((m) => m.words),
      used,
      MAX_BROADER,
    );
    const others = [...similar, ...narrower].sort((a, b) => (scores.get(b) ?? 0) - (scores.get(a) ?? 0));
    const candidates = [...synonyms, ...others].filter((w) => !isName(w));
    return { meaning, reading, synonyms, similar, narrower, broader, opposites: this.wordNet.opposites(meaning, reading.lemma), candidates };
  }

  /** The words of `meanings`, likeliest substitutes first. */
  private ranked(meanings: readonly Meaning[], reading: Reading, scores: Map<string, number>): string[] {
    const local = new Map<string, number>();
    for (const meaning of meanings) {
      for (const word of meaning.words) {
        const score = this.score(word, meaning, reading);
        local.set(word, Math.max(local.get(word) ?? -Infinity, score));
      }
    }
    for (const [word, score] of local) scores.set(word, Math.max(scores.get(word) ?? -Infinity, score));
    return [...local.keys()].sort((a, b) => local.get(b)! - local.get(a)! || a.split(' ').length - b.split(' ').length || javaCompare(a.toLowerCase(), b.toLowerCase()));
  }

  /** How likely `word` is to be a good substitute, through `meaning` (`Thesaurus.score`). */
  private score(word: string, meaning: Meaning, reading: Reading): number {
    const lemma = wordKey(reading.lemma);
    const asThis = this.wordNet.meaningsOf(word, meaning.partOfSpeech);
    const rank = asThis.findIndex((m) => m.id === meaning.id);
    const all = this.wordNet.meaningCount(word);
    let score = (rank === 0 ? 4 : rank === 1 ? 1 : 0) + (asThis.length === 1 ? 2 : 0) + (asThis.length * 2 >= all ? 3 : 0);
    if (word.includes(' ')) {
      score -= 2;
    } else {
      const frequency = this.frequency(word, meaning.partOfSpeech, reading.inflection);
      score += Math.min(10, frequency > 0 ? frequency : Math.min(6, all));
    }
    const key = wordKey(word);
    if (key !== lemma && key.includes(lemma)) score -= 6;
    if (isName(word)) score -= 20;
    return score;
  }

  private frequency(word: string, pos: PartOfSpeech, inflection: Inflection): number {
    if (pos === 'VERB') return this.forms.frequency(this.inflectWord(word, pos, 'PRESENT_PARTICIPLE'));
    if (pos === 'NOUN' && inflection === 'PLURAL') return this.forms.frequency(this.inflectWord(word, pos, inflection));
    return this.forms.frequency(word);
  }

  private inflect(word: string, reading: Reading): string {
    const { partOfSpeech: pos, inflection } = reading;
    const degree = inflection === 'COMPARATIVE' || inflection === 'SUPERLATIVE';
    if (word.includes(' ')) {
      if (degree) return `${inflection === 'COMPARATIVE' ? 'more ' : 'most '}${word}`;
      if (pos === 'VERB') {
        const space = word.indexOf(' ');
        return this.inflectWord(word.slice(0, space), pos, inflection) + word.slice(space);
      }
      if (pos === 'NOUN' && inflection === 'PLURAL') {
        const parts = word.split(' ');
        let head = parts.length - 1;
        for (let i = 1; i < parts.length; i++) {
          if (LINKS.has(parts[i]!.toLowerCase())) {
            head = i - 1;
            break;
          }
        }
        parts[head] = this.inflectWord(parts[head]!, pos, inflection);
        return parts.join(' ');
      }
      return word;
    }
    return this.inflectWord(word, pos, inflection);
  }

  /** The commonest of the forms the dictionary knows; a comparative nobody uses becomes "more ...". */
  private inflectWord(word: string, pos: PartOfSpeech, inflection: Inflection): string {
    let best: string | null = null;
    let bestFrequency = -1;
    for (const form of this.forms.inflect(word, pos, inflection)) {
      const frequency = this.forms.frequency(form);
      if (frequency > bestFrequency) {
        best = form;
        bestFrequency = frequency;
      }
    }
    const degree = inflection === 'COMPARATIVE' || inflection === 'SUPERLATIVE';
    if (best !== null && !(degree && bestFrequency === 0 && this.forms.frequency(word) > 0)) return best;
    return regularForm(word, pos, inflection);
  }
}

/** Lower is likelier, when one word could be several forms of the same verb. */
function preferred(inflection: Inflection, cue: string): number {
  switch (inflection) {
    case 'PAST_PARTICIPLE':
      return PARTICIPLE_CUES.has(cue) ? 0 : 2;
    case 'PAST':
      return 1;
    case 'BASE':
      return VERB_CUES.has(cue) ? 0 : 3;
    default:
      return 1;
  }
}

function scoreReading(reading: Reading, lower: string, cue: string): number {
  let score = 0;
  const pos = reading.partOfSpeech;
  if (NOUN_CUES.has(cue)) score += pos === 'NOUN' ? 3 : pos === 'ADJECTIVE' ? 2 : 0;
  if (VERB_CUES.has(cue) && pos === 'VERB') score += 3;
  if (PARTICIPLE_CUES.has(cue) && pos === 'VERB' && reading.inflection !== 'BASE') score += reading.inflection === 'PRESENT_PARTICIPLE' ? 5 : 2;
  if (MODIFIER_CUES.has(cue) && (pos === 'ADJECTIVE' || pos === 'ADVERB')) score += 3;
  if (reading.lemma === lower) score += 1;
  return score;
}

/** `words` not already `used` (which it then adds to), at most `limit`. */
function fresh(words: readonly string[], used: Set<string>, limit: number): string[] {
  const out: string[] = [];
  for (const word of words) {
    if (out.length >= limit) break;
    const key = wordKey(word);
    if (!used.has(key)) {
      used.add(key);
      out.push(word);
    }
  }
  return out;
}

// ---- for menus (`Sense.offered`, `gist`; `Lookup.isEmpty`, `leading`) ----

export const offered = (sense: Sense, limit: number) => sense.candidates.slice(0, limit);

export const hasWords = (sense: Sense) => sense.candidates.length > 0;

export const isEmptyLookup = (lookup: Lookup) => !lookup.senses.some(hasWords);

/** A short heading: the gist of the definition, cut at a word boundary to about `length` characters. */
export function gist(sense: Sense, length: number): string {
  let definition = sense.meaning.definition;
  const semicolon = definition.indexOf(';');
  if (semicolon > 0) definition = definition.slice(0, semicolon);
  definition = javaStrip(definition.replace(/^\([^)]*\)\s*/, ''));
  if (definition.length <= length) return definition;
  const cut = definition.lastIndexOf(' ', length);
  return `${javaStrip(cut > length / 2 ? definition.slice(0, cut) : definition.slice(0, length))}…`;
}

/** The few meanings a short menu shows: up to four of the likeliest reading and two of each other, then more, to `max`. */
export function leading(lookup: Lookup, max: number): Sense[] {
  const byReading = new Map<string, Sense[]>();
  for (const sense of lookup.senses) {
    if (!hasWords(sense)) continue;
    const key = readingKey(sense.reading);
    if (!byReading.has(key)) byReading.set(key, []);
    byReading.get(key)!.push(sense);
  }
  const chosen: Sense[] = [];
  let first = true;
  for (const group of byReading.values()) {
    const allowance = first ? 4 : 2;
    first = false;
    for (let i = 0; i < Math.min(allowance, group.length) && chosen.length < max; i++) chosen.push(group[i]!);
  }
  for (const group of byReading.values()) {
    for (const sense of group) {
      if (chosen.length >= max) break;
      if (!chosen.includes(sense)) chosen.push(sense);
    }
  }
  return chosen.sort((a, b) => lookup.senses.indexOf(a) - lookup.senses.indexOf(b));
}
